import { useState } from 'react';

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterGroup {
  key: string;
  label: string;
  options: FilterOption[];
}

interface FilterBarProps {
  groups: FilterGroup[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  sort: string;
  onSortChange: (value: string) => void;
  sortOptions: FilterOption[];
  onReset: () => void;
}

export default function FilterBar({
  groups,
  values,
  onChange,
  sort,
  onSortChange,
  sortOptions,
  onReset,
}: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const renderControls = () => (
    <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center">
      {groups.map((group) => (
        <label key={group.key} className="flex items-center gap-2">
          <span className="whitespace-nowrap text-sm text-foreground-500">
            {group.label}
          </span>
          <select
            value={values[group.key] ?? '全部'}
            onChange={(event) => onChange(group.key, event.target.value)}
            className="min-w-[110px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
          >
            {group.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      ))}

      <label className="flex items-center gap-2">
        <span className="whitespace-nowrap text-sm text-foreground-500">排序</span>
        <select
          value={sort}
          onChange={(event) => onSortChange(event.target.value)}
          className="min-w-[110px] cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
        >
          {sortOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <button
        type="button"
        onClick={onReset}
        className="inline-flex w-fit cursor-pointer items-center gap-1 rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-accent-300 hover:text-accent-600"
      >
        <i className="ri-refresh-line" aria-hidden="true" />
        重置筛选
      </button>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200/80 bg-background-50 p-4 md:p-5">
      <div className="hidden md:block">{renderControls()}</div>

      <div className="md:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((prev) => !prev)}
          className="flex w-full cursor-pointer items-center justify-between rounded-md bg-background-100 px-4 py-2.5 text-sm font-medium text-foreground-800"
        >
          <span className="flex items-center gap-2">
            <i className="ri-filter-3-line text-base" aria-hidden="true" />
            筛选与排序
          </span>
          <i className={mobileOpen ? 'ri-arrow-up-s-line text-lg' : 'ri-arrow-down-s-line text-lg'} aria-hidden="true" />
        </button>
        {mobileOpen && <div className="mt-4">{renderControls()}</div>}
      </div>
    </div>
  );
}